import { useEffect, useState } from 'react'

import type { GetStaticPropsContext, GetStaticPropsResult } from 'next'
import { useSession } from 'next-auth/react'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'

import membersMarketData from '@data/memberMarket.json'

import getGtmEcommerceProduct from '@lib/analytics/ecommerce'
import { gtmEvent } from '@lib/analytics/gtm'
import { getProductBySlug } from '@lib/operations/product'
import useSubscriptions from '@lib/subscription-box/use-subscriptions'
import type { Product, Product as ProductType } from '@lib/types'
import { isCustomizableBoxVariant } from '@lib/utils/isSubscriptionBox'
import { isMembersMarketOpen } from '@lib/utils'

import Spinner from '@components/Spinner'
import ProductView from '@components/product/ProductView'

interface Props {
	slug: string
}

export default function MembersMarketProductPage({ slug }: Props) {
	const [product, setProduct] = useState<ProductType>()
	const { loading: subscriptionsLoading, subscriptions } = useSubscriptions()
	const [loading, setLoading] = useState(true)
	const router = useRouter()
	const { data: session } = useSession()

	// Check for force market open URL parameter
	const forceMarketOpenParam = router.query.forcemarketopen as string | undefined
	const { isCurrentlyOpen: isMarketOpen } =
		isMembersMarketOpen(membersMarketData, forceMarketOpenParam)

	const accessToken = (session as any)?.shopifyAccessToken
	const isLoggedIn = session !== null && accessToken
	const membersMarketAccessRaw = (membersMarketData as any).members_market_access
	// Handle empty strings, null, undefined - default to 'Active Subscription Only'
	const membersMarketAccess = (membersMarketAccessRaw && membersMarketAccessRaw.trim()) || 'Active Subscription Only'

	// Check if there's an active subscription or one with queued charges (matches index.tsx logic)
	const hasSubscriptionOrder = subscriptions?.some(
		(sub) => sub.status === 'active' || sub.has_queued_charges
	)

	// Determine if user has access based on members_market_access setting
	const hasAccess = () => {
		if (membersMarketAccess === 'All Can Access') {
			return true // Everyone can access
		}
		if (membersMarketAccess === 'Logged in Only') {
			return isLoggedIn // Only logged-in users
		}
		// Default: 'Active Subscription Only'
		return hasSubscriptionOrder // Only users with active subscriptions
	}

	// LOAD PAGE
	useEffect(() => {
		const fetchProductData = async (): Promise<void> => {
			try {
				const p = await getProductBySlug(slug ?? '')
				if (p) setProduct(p)
			} catch (error) {
				// TODO: SHOW 404 PRODUCT
			} finally {
				setLoading(false)
			}
		}

		// Check if user should have access based on market dates/override and access setting
		const canAccess = isMarketOpen && hasAccess()

		if (canAccess) {
			fetchProductData()
		} else {
			// Redirect to members market page if market is closed or user doesn't have access
			router.push('/members-market')
		}
	}, [isMarketOpen, membersMarketAccess, isLoggedIn, hasSubscriptionOrder, slug, router])

	// GTM
	useEffect(() => {
		if (product) {
			// Analytics *****
			const gtmEcommerceProduct = getGtmEcommerceProduct(
				product.variants && product.variants.length > 0
					? {
							product,
							price: product.variants[0].price.amount,
							// productBreadcrumb: breadcrumbs,
							isSubscriptionBox: !!isCustomizableBoxVariant(product.variants[0].id)
						}
					: {
							product,
							price: '',
							// productBreadcrumb: breadcrumbs,
							isSubscriptionBox: false
						}
			)

			gtmEvent({
				event: 'analytics_event',
				data: {
					eventData: {
						category: 'ecommerce',
						action: 'productDetail'
					},
					ecommerce: {
						currencyCode: product.variants[0].price.currencyCode,
						detail: {
							// TODO: breadcrumbs non available
							actionField: { list: 'members-market' },
							products: [gtmEcommerceProduct]
						}
					}
				}
			})
		}
		// Analytics *****
	}, [product])

	// TODO: add loader if while loading product

	if (loading || subscriptionsLoading)
		return (
			<div className="fixed inset-0 z-50 flex items-center justify-center bg-white/50 pointer-events-none">
				<Spinner />
			</div>
		)

	return product ? (
		<>
			<NextSeo title={product.name} />

			{/* Why is this a component? It's only used here */}
			<ProductView
				product={product}
				showFavorite={false}
				backButton={{ path: '/members-market', title: 'Members Market' }}
			/>
		</>
	) : null
	// TODO: what to do if the product has not loaded
}

export async function getStaticProps({
	params
}: Required<GetStaticPropsContext<{ slug: Product['slug'] }>>): Promise<
	GetStaticPropsResult<Props>
> {
	// Note: Server-side redirect removed to allow forcemarketopen URL parameter to work.
	// Client-side redirect logic in the component handles access control.
	return {
		props: { slug: params.slug }
	}
}

export async function getStaticPaths() {
	// Pages are refreshed using on-demand ISR
	return {
		paths: [],
		fallback: 'blocking'
	}
}
